/**
 * 제품 멤버 수 설정 모달 컴포넌트
 * 제품의 최대 이용자 수를 설정하는 모달 컴포넌트입니다.
 * 현재 이용자 수를 고려하여 최대 이용자 수를 설정할 수 있습니다.
 */

import { useEffect, useRef, useState } from "react";
import { BaseButton, BaseInput } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import { onlyNumber } from "src/utils";

/**
 * 제품 멤버 수 설정 모달 컴포넌트의 props 인터페이스
 */
type Props = {
  onClose: () => void; // 모달 닫기 함수
  onSubmit: (members: number) => void; // 설정된 멤버 수 적용 함수
  currentMembers: number; // 현재 이용자 수
};

/**
 * 에러 정보 인터페이스
 */
type Error = {
  type?: string; // 에러 타입
  message?: string; // 에러 메시지
};

/**
 * 제품 멤버 수 설정 모달 컴포넌트
 * @param onClose 모달 닫기 함수
 * @param onSubmit 설정된 멤버 수 적용 함수
 * @param currentMembers 현재 이용자 수
 */
const ProductMemberModal = ({ onClose, onSubmit, currentMembers }: Props) => {
  const [memberNum, setMemberNum] = useState(""); // 설정할 멤버 수
  const inputRef = useRef<HTMLInputElement>(null); // 입력 필드 참조

  const [error, setErrorError] = useState<Error>(); // 에러 상태

  // 컴포넌트 마운트 시 입력 필드에 포커스
  useEffect(() => {
    inputRef && inputRef.current?.focus();
  }, []);

  return (
    <BaseAbstractModal size="medium" isOpen={true}>
      <section className="base-abstract-modal__contents">
        {/* 모달 헤더 */}
        <section className="base-abstract-modal__title">
          <h1>최대 인원 설정</h1>
        </section>

        {/* 멤버 수 입력 섹션 */}
        <section className="base-abstract-modal__contents px30">
          <div className="mb20">
            <span className="font18">최대 이용자</span>
          </div>
          <div className="flex-center">
            <BaseInput
              inputRef={inputRef}
              maxLength={error?.type ? 1 : 8}
              className="minmax220"
              onChange={(value: string) => {
                if (value !== "" && Number(value) < 1) {
                  setErrorError({ type: "moreMember", message: "한 명 이상 입력 주세요." });
                } else {
                  setErrorError({ type: "", message: "" });
                }
                setMemberNum(onlyNumber(value));
              }}
              value={memberNum}
            />
            <span className="font18 ml5">명</span>
          </div>
          {error && <p className="validation-text mt5">{error.message}</p>}
        </section>

        {/* 버튼 섹션 */}
        <section className="base-abstract-modal__btn-wrap">
          <BaseButton title={"취소"} className="color-white" onClick={onClose} />
          <BaseButton
            title="적용"
            onClick={() => {
              if (Number(currentMembers) > Number(memberNum)) {
                setErrorError({ message: "현재 이용자 수 이상을 입력해 주세요." });
              } else {
                setErrorError({ type: "", message: "" });
                onSubmit(Number(memberNum));
              }
            }}
          />
        </section>
      </section>
    </BaseAbstractModal>
  );
};

export default ProductMemberModal;
